// Always-on banner shown across the app while maintenance is active.
// Read-only badge + reason + ETA + admin shortcut.

import { Link } from 'react-router-dom';
import { Wrench, Lock } from 'lucide-react';
import { useWhoami } from '../api/client';

interface Props {
  readonly reason?: string;
  readonly until?: string;
}

function fmtUntil(until?: string): string | null {
  if (!until) return null;
  try {
    const d = new Date(until);
    return d.toLocaleString();
  } catch {
    return null;
  }
}

export function MaintenanceBanner({ reason, until }: Props) {
  const { user } = useWhoami();
  const isAdmin = user?.roles?.includes('org_admin') || user?.roles?.includes('admin');
  const eta = fmtUntil(until);

  return (
    <div className="border-b border-warning/30 bg-warning/10">
      <div className="px-6 py-3 flex items-start gap-3 text-sm">
        <Wrench className="h-4 w-4 mt-0.5 text-warning shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-fg">Maintenance mode</span>
            <span className="orb-badge bg-warning/20 text-warning border-warning/40 inline-flex items-center gap-1">
              <Lock className="h-3 w-3" />
              read-only
            </span>
            {eta && (
              <span className="text-fg-muted text-xs">
                until <span className="font-medium text-fg">{eta}</span>
              </span>
            )}
          </div>
          {reason && <p className="text-fg-muted mt-1">{reason}</p>}
        </div>
        {isAdmin && (
          <Link
            to="/admin/maintenance"
            className="text-sm text-warning hover:underline whitespace-nowrap"
          >
            Manage →
          </Link>
        )}
      </div>
    </div>
  );
}
